import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLocationStore } from '@/store/location';
import { AddressSearchModal } from '@/components/common/AddressSearchModal';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useGeolocation } from '@/hooks/useGeolocation';

export function DesktopAddressSelectPage() {
  const navigate = useNavigate();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  
  const {
    savedAddresses,
    selectedAddressId,
    selectAddress,
    removeSavedAddress
  } = useLocationStore();
  
  const {
    isSupported: isGeolocationSupported,
    requestPermission,
    getCurrentPosition
  } = useGeolocation();

  const handleSearchAddress = () => {
    setIsSearchModalOpen(true);
  };

  const handleAddressSelected = (selectedAddress: any) => {
    setIsSearchModalOpen(false);
    navigate('/address-detail', { state: { selectedAddress } });
  };

  const handleSelectSavedAddress = async (addressId: string) => {
    try {
      await selectAddress(addressId);
      navigate('/');
    } catch (error) {
      console.error('Failed to select address:', error);
      alert('주소 선택에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleDeleteAddress = async (e: React.MouseEvent, addressId: string) => {
    e.stopPropagation();
    
    if (confirm('이 주소를 삭제하시겠습니까?')) {
      try {
        await removeSavedAddress(addressId);
      } catch (error) {
        console.error('Failed to delete address:', error);
        alert('주소 삭제에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const handleMapLocationSelect = async () => {
    if (!isGeolocationSupported) {
      alert('이 브라우저에서는 위치 기능을 사용할 수 없습니다.');
      return;
    }

    try {
      setIsRequestingLocation(true);
      
      const permissionState = await requestPermission();
      
      if (permissionState === 'denied') {
        alert('위치 권한이 거부되었습니다.\n브라우저 설정에서 위치 권한을 허용해주세요.');
        return;
      }
      
      const position = await getCurrentPosition();
      
      navigate('/map-location-select', {
        state: {
          coordinate: {
            latitude: position.latitude,
            longitude: position.longitude
          }
        }
      });
      
    } catch (error) {
      console.error('위치 가져오기 실패:', error);
      navigate('/map-location-select');
    } finally {
      setIsRequestingLocation(false);
    }
  };

  return (
    <DesktopLayout>
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <img src="/icons/icon_back.svg" alt="뒤로가기" width="28" height="28" />
              </button>
              <h1 className="text-2xl font-bold text-[#222222]">주소 설정</h1>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              {/* Left Column - Search & Map */}
              <div className="space-y-6">
                {/* Search Button */}
                <div>
                  <h2 className="text-lg font-semibold text-[#222222] mb-3">주소 검색</h2>
                  <button
                    onClick={handleSearchAddress}
                    className="w-full h-14 bg-[#F5F5F5] border border-[#222222] rounded-lg flex items-center px-4 text-left hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-[#222222] text-lg flex-1">검색어를 입력해주세요</span>
                    <img src="/icons/common_home_search_button.svg" alt="검색" width="28" height="28" className="ml-3" />
                  </button>
                </div>

                {/* Map Location Button */}
                <div>
                  <h2 className="text-lg font-semibold text-[#222222] mb-3">현재 위치</h2>
                  <button
                    onClick={handleMapLocationSelect}
                    disabled={isRequestingLocation}
                    className="w-full h-14 bg-[#DEDEDE] rounded-lg flex items-center justify-center gap-3 hover:bg-gray-300 transition-colors disabled:opacity-50"
                  >
                    {isRequestingLocation ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#222222]"></div>
                        <span className="text-lg font-semibold text-[#222222]">위치 권한 요청 중...</span>
                      </>
                    ) : (
                      <>
                        <img src="/icons/icon_gps.svg" alt="GPS" width="32" height="32" />
                        <span className="text-lg font-semibold text-[#222222]">지도에서 위치 찾기</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Right Column - Saved Addresses */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-[#222222]">저장된 주소</h2>
                  <span className="text-sm text-gray-600">{savedAddresses.length}/3개</span>
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {savedAddresses.length === 0 ? (
                    <div className="text-center py-16 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                      <div className="text-lg mb-2">저장된 주소가 없습니다</div>
                      <div className="text-sm">주소를 검색해서 추가해보세요</div>
                    </div>
                  ) : (
                    savedAddresses.map((address) => (
                      <div
                        key={address.id}
                        className={cn(
                          "p-4 rounded-lg relative transition-all",
                          address.isDefault || selectedAddressId === address.id
                            ? "bg-[#86DB89] shadow-md"
                            : "bg-[#F5F5F5] hover:bg-gray-200"
                        )}
                      >
                        <button
                          onClick={() => handleSelectSavedAddress(address.id)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start gap-4">
                            {/* Checkbox */}
                            <div className={cn(
                              "w-5 h-5 rounded border flex items-center justify-center mt-0.5 flex-shrink-0",
                              address.isDefault || selectedAddressId === address.id
                                ? "bg-white border-white"
                                : "border-[#222222]"
                            )}>
                              {(address.isDefault || selectedAddressId === address.id) && (
                                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                                  <path
                                    d="M10 3L4.5 8.5L2 6"
                                    stroke="#222222"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>

                            {/* Address Info */}
                            <div className="flex-1 min-w-0 pr-8">
                              <div className="text-lg font-bold text-[#000000] mb-2">
                                {address.alias}
                              </div>
                              <div className="text-base text-[#000000] break-words leading-relaxed">
                                {address.address}
                                {address.detailAddress && ` ${address.detailAddress}`}
                              </div>
                            </div>
                          </div>
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={(e) => handleDeleteAddress(e, address.id)}
                          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#666666] hover:text-red-500 transition-colors rounded-lg hover:bg-white/20"
                          aria-label="주소 삭제"
                        >
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M12 4L4 12M4 4L12 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddressSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onAddressSelected={handleAddressSelected}
      />
    </DesktopLayout>
  );
}